<template>
    <div class="review-club-detail__main-box">
        <ReviewRatingStars :review="review" />

        <div v-if="review.product.sellingName" class="mb-1 fs-6">
            <span class="text-gray">{{ `${$t('productDetail.rating.variant')} ${review.product.sellingName}` }}</span>
        </div>

        <h3 class="review-club-detail__title review-club-detail__main-box mt-3 mb-0 p-0">
            {{ review.title }}
        </h3>

        <p class="review-club-detail__introduction-text">{{ review.introductionText }}</p>

        <div class="row mb-4">
            <div class="col-6">
                <p v-for="pros in review.pros" :key="pros" class="d-flex align-items-center mb-1">
                    <img
                        v-lazy-load
                        src="/nuxt-assets/images/lazy-load-error.svg"
                        data-src="/nuxt-assets/images/icons/plus.svg"
                        class="plus-minus-icon"
                        width="25"
                        :alt="pros"
                    />
                    {{ pros }}
                </p>
            </div>

            <div class="col-6">
                <p v-for="cons in review.cons" :key="cons" class="d-flex align-items-center mb-1">
                    <img
                        v-lazy-load
                        src="/nuxt-assets/images/lazy-load-error.svg"
                        data-src="/nuxt-assets/images/icons/minus.svg"
                        class="plus-minus-icon"
                        width="25"
                        :alt="cons"
                    />
                    {{ cons }}
                </p>
            </div>
        </div>

        <div class="row align-items-center mb-4">
            <div v-for="(reviewImage, slideNumber) in allReviewImages" :key="slideNumber" class="col-auto cursor-point">
                <img
                    :src="reviewImage.path"
                    :alt="review.product.name || ''"
                    class="review-club-detail__carousel-preview-image"
                    loading="lazy"
                    @click="openModal(slideNumber)"
                />
            </div>

            <Modal modal-name="modal-review-images-carousel" modal-class="modal-max">
                <Carousel v-if="allReviewImages.length > 1" v-model="slide" :aria-controls="true">
                    <Slide v-for="image in allReviewImages" :key="image.path" class="text-center">
                        <img :src="image.path" :alt="`review.product.id`" class="review-club-detail__carousel-image" loading="lazy" />
                    </Slide>
                </Carousel>
            </Modal>
        </div>

        <div v-if="review.richContentHtml" class="row mb-4">
            <div v-for="questionBlock in review.questionBlocks" :key="questionBlock.title" class="col">
                <a v-for="image in questionBlock.images.flat()" :key="image.path" class="d-block mb-4">
                    <img
                        class="img-fluid cursor-point"
                        height="120"
                        :src="image.path"
                        :alt="questionBlock.title"
                        loading="lazy"
                        @click="openModal(findImageIndexBySrc(image.path) || 0)"
                    />
                </a>
            </div>
        </div>

        <div v-for="(questionBlock, index) in review.questionBlocks" :key="index" class="row mb-4 align-items-stretch">
            <div class="col-12 col-md-7 align-self-center js_block_text_container">
                <h4 v-if="questionBlock.title" class="review-club-detail__block-title">{{ questionBlock.title }}</h4>
                <div v-for="(question, i) in questionBlock.questions" :key="i">
                    <p v-if="question" class="mt-4 fwb">{{ question.question }}</p>
                    <p>{{ question.answer }}</p>
                </div>
            </div>

            <div
                v-if="questionBlock.images.length"
                class="col-12 col-md-5 text-center gallery_container d-flex flex-column justify-content-center"
            >
                <a v-for="image in questionBlock.images.flat()" :key="image.path" class="d-block mb-4">
                    <img
                        class="img-fluid cursor-point"
                        :src="image.path"
                        :alt="questionBlock.title"
                        loading="lazy"
                        @click="openModal(findImageIndexBySrc(image.path) || 0)"
                    />
                </a>
            </div>

            <div v-if="questionBlock.quotedText" class="row text-primary fs-24 text-center mx-auto justify-content-center my-4 fst-italic">
                <div class="col-auto align-self-end">
                    <img src="/nuxt-assets/images/icons/quote-start.svg" class="me-4" :alt="questionBlock.quotedText" loading="lazy" />
                </div>
                <div class="col py-4 lh-base">{{ questionBlock.quotedText }}</div>
                <div class="col-auto">
                    <img src="/nuxt-assets/images/icons/quote-end.svg" class="me-4" :alt="questionBlock.quotedText" loading="lazy" />
                </div>
            </div>
        </div>

        <div class="d-flex flex-wrap align-items-center fs-12 mt-2 position-relative justify-content-center justify-content-md-end ml-auto">
            <div class="product-prev__like-box__tooltip product-prev__like-box__tooltip--hidden" />

            <span class="btn btn-text fs-12" @click="rateComment(review, 'like')">
                <img src="/nuxt-assets/images/icons/like.svg" :alt="`review.product.id`" loading="lazy" />
                {{ $t('reviewClub.like') }} (<span>{{ review.likesCount + like }}</span
                >)
            </span>

            <span class="btn btn-text fs-12" @click="rateComment(review, 'dislike')">
                <img src="/nuxt-assets/images/icons/dislike.svg" :alt="`review.product.id`" loading="lazy" />
                {{ $t('reviewClub.unlike') }} (<span>{{ review.dislikesCount + dislike }}</span
                >)
            </span>

            <div class="text-right w-100 mt-3">
                <button class="btn btn-primary btn-sm py-1" @click="$bus.$emit('closeReview')">
                    {{ $t('reviewClub.backToList') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { PropType } from 'vue'
import { Carousel, Slide } from 'vue3-carousel'
import Review from '~/models/ReviewClub/Review'
import ReviewRatingStars from '~/components/objects/ReviewClub/ReviewRatingStars.vue'
import QuestionBlockImage from '~/models/ReviewClub/QuestionBlockImage'
import Modal from '~/components/Modal.vue'
import { useAuthStore } from '~/stores/auth'

export default {
    components: {
        Modal,
        ReviewRatingStars,
        Carousel,
        Slide,
    },

    props: {
        review: {
            type: Object as PropType<Review>,
            required: true,
        },

        allReviewImages: {
            type: Array as PropType<QuestionBlockImage[]>,
            required: true,
        },
    },

    data() {
        return {
            like: 0,
            dislike: 0,
            slide: 0,
        }
    },

    methods: {
        async rateComment(comment: Review, isUseful: 'like' | 'dislike'): Promise<void> {
            const authStore = useAuthStore()
            const data = {
                reviewId: comment.id,
                type: isUseful,
                accessToken: authStore.accessToken,
            }

            if (!data.accessToken) {
                useNuxtApp().$bus.$emit('showModal', `ask-question-login-${comment.id}-detail`)
            } else {
                this.like = isUseful === 'like' ? 1 : 0
                this.dislike = isUseful === 'dislike' ? 1 : 0
            }

            this.loading = true

            try {
                await this.$repository.rateReviewClub(data)
            } catch {}

            this.loading = false
        },

        findImageIndexBySrc(src: string): number | null {
            for (const [imageIndex, image] of this.allReviewImages.entries()) {
                if (image.path === src) {
                    return imageIndex
                }
            }

            return null
        },

        openModal(slideNumber: number): void {
            this.slide = slideNumber
            useNuxtApp().$bus.$emit('showModal', 'modal-review-images-carousel')
        },
    },
}
</script>

<style lang="scss" scoped>
@import '../../../../node_modules/bootstrap/scss/functions';
@import '../../../../node_modules/bootstrap/scss/variables';
@import '../../../../node_modules/bootstrap/scss/mixins';

.review-club-detail {
    &__carousel-preview-image {
        height: 120px;
    }

    &__carousel-image {
        max-height: 80vh;
    }

    &__title {
        font-size: 19px;
        margin-top: 10px;
        margin-bottom: 13px;
    }

    &__block-title {
        font-size: 18px;
        font-weight: 700;
        width: 100%;
        margin-bottom: 1em;
    }

    &__user-info {
        width: calc(100% - 92px);
        padding-left: 1rem;
    }

    &__main-box {
        width: 100%;
        padding: 13px;
        display: flex;
        flex-direction: column;
    }
}

.plus-minus-icon {
    height: 17px;
    width: 17px;
    display: inline-block;
    margin-right: 8px;
}
</style>
